/**
 * Recently Seen products.
 *
 * Adds a client-side-only "Recently seen" tab alongside the server-rendered
 * "You may also like" recommendations in sections/product-recommendations.liquid.
 *
 * - Tracks viewed product handles in localStorage (no app/backend needed).
 * - For each handle, fetches the REAL rendered <x-grid><product-card>...
 *   markup via the Section Rendering API:
 *   /products/{handle}?section_id=recently-viewed-card.
 *   sections/recently-viewed-card.liquid wraps {% render 'c-prod-card',
 *   product: product %} in the theme's own x-grid, using the exact same
 *   attributes as sections/product-recommendations.liquid's grid. The grid
 *   wrapper's attributes don't depend on `product` at all, so every fetch
 *   produces an identical one - the FIRST fetched grid is kept as-is, and
 *   every other fetch's <product-card> is moved into it. This guarantees
 *   spacing/borders match the real recommendations grid exactly, without
 *   this script re-deriving grid attributes (like cardGridGap, which is a
 *   boolean toggle rather than a plain CSS length) itself.
 * - Applies the same horizontal-scroll/carousel classes as the Liquid
 *   section (see sections/product-recommendations.liquid) so the
 *   "Recently seen" grid always matches the flush, no-gap, divided-border
 *   look of "You may also like" - unlike the Liquid section, this is NOT
 *   gated behind a >3 product count: that gate exists there purely to
 *   skip the scroll wrapper when it'd be pointless, but "Recently seen"
 *   will often have 2-3 entries and, without the carousel classes, falls
 *   back to a plain CSS grid that honours the theme's sitewide
 *   cardGridGap setting - real gaps, a full border box per card, and
 *   empty trailing grid columns - which looks inconsistent with the
 *   "You may also like" tab right next to it. hscroll-slider already
 *   hides its own prev/next arrows when there's nothing to scroll (see
 *   assets/m-hscroll.js's isScrollable check), so applying the carousel
 *   classes unconditionally is safe even with only 1-2 cards.
 * - Uses a MutationObserver on <product-recommendations> because its
 *   content is itself swapped in asynchronously by the theme's own
 *   recommendations fetch (data-url) once real recommendations load -
 *   tabs are (re)wired every time that swap happens, not just once on
 *   DOMContentLoaded.
 * - hscroll-slider (assets/m-hscroll.js) measures its scrollable width once,
 *   on connectedCallback. The "Recently seen" panel starts out `hidden`
 *   (display: none) until its tab is selected, so that first measurement
 *   comes back as zero-width and the component concludes there's nothing
 *   to scroll - it never shows the arrows again until something else
 *   forces a re-measure (the same class of bug assets/custom-hscroll-
 *   refresh.js already fixes for sliders inside a closed dialog). Firing
 *   a window resize right after the panel is actually unhidden re-triggers
 *   hscroll-slider's own resize listener so it re-measures with real
 *   dimensions and the prev/next arrows appear instead of falling back to
 *   a plain native scrollbar.
 */
(function () {
    var STORAGE_KEY = 'dani:recentlyViewedProducts';
    var MAX_STORED = 12;
    var MAX_SHOWN = 8;
    var CARD_SECTION_ID = 'recently-viewed-card';
    var cardStylesInjected = false;

    function getStoredHandles() {
        try {
            var raw = window.localStorage.getItem(STORAGE_KEY);
            var list = raw ? JSON.parse(raw) : [];
            return Array.isArray(list) ? list : [];
        } catch (e) {
            return [];
        }
    }

    function setStoredHandles(list) {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
        } catch (e) {
            // localStorage unavailable (private mode, storage full, etc.) - fail silently
        }
    }

    function recordCurrentProduct(handle) {
        if (!handle) return;
        var list = getStoredHandles().filter(function (h) {
            return h !== handle;
        });
        list.unshift(handle);
        setStoredHandles(list);
    }

    function pruneHandles(invalidHandles) {
        if (!invalidHandles.length) return;
        var list = getStoredHandles().filter(function (h) {
            return invalidHandles.indexOf(h) === -1;
        });
        setStoredHandles(list);
    }

    // Re-measures every hscroll-slider on the page so its prev/next arrows
    // reflect real dimensions - needed after unhiding a panel that was
    // display:none when its hscroll-slider first connected. See the
    // top-of-file note and assets/custom-hscroll-refresh.js for the same
    // fix applied to sliders inside a closed dialog.
    function refreshHscrollSliders() {
        requestAnimationFrame(function () {
            window.dispatchEvent(new Event('resize'));
        });
    }

    // Injects c-prod-card.liquid's own embedded <style> block (hover/quick-buy
    // positioning rules) once, in case the "recommended" panel never rendered
    // a real card on this page load (e.g. no recommendations available) and
    // so never loaded it itself.
    function ensureCardStyles(doc) {
        if (cardStylesInjected) return;
        var style = doc.querySelector('style');
        if (style) {
            var clone = style.cloneNode(true);
            clone.setAttribute('data-recently-viewed-card-style', '');
            document.head.appendChild(clone);
        }
        cardStylesInjected = true;
    }

    // Fetches the real rendered <x-grid><product-card>...</x-grid> for a
    // handle via the Section Rendering API against that product's own
    // page, so Liquid has a genuine `product` object - see
    // sections/recently-viewed-card.liquid for why this works without any
    // custom query-param support.
    function fetchCardMarkup(routesRoot, handle) {
        var url = routesRoot.replace(/\/$/, '') + '/products/' + handle + '?section_id=' + CARD_SECTION_ID;
        return fetch(url)
            .then(function (res) {
                return res.ok ? res.text() : null;
            })
            .then(function (html) {
                if (!html) return null;
                var doc = new DOMParser().parseFromString(html, 'text/html');
                var grid = doc.querySelector('x-grid');
                var card = doc.querySelector('product-card');
                if (!grid || !card) return null;
                ensureCardStyles(doc);
                return { grid: grid, card: card };
            })
            .catch(function () {
                return null;
            });
    }

    // Resolves which carousel classes to apply for a given horizontalScroller
    // setting. Unlike sections/product-recommendations.liquid's own
    // `case scrollerState` logic, this intentionally does NOT gate on
    // product count - see the file header for why.
    function resolveScroll(scrollerSetting) {
        var horizontalScroll = null;
        var hscrollSlide = null;

        switch (scrollerSetting) {
            case 'desk':
                horizontalScroll = 'card scroll-snap';
                hscrollSlide = true;
                break;
            case 'mob':
                horizontalScroll = 'card scroll-snap-m';
                hscrollSlide = true;
                break;
            case 'both':
                horizontalScroll = 'card scroll-snap scroll-snap-m';
                hscrollSlide = true;
                break;
        }

        return { horizontalScroll: horizontalScroll, hscrollSlide: hscrollSlide };
    }

    // `grid` is a real, server-rendered <x-grid> (see fetchCardMarkup) with
    // all its cards already inside it. This only adds the horizontal-scroll
    // classes/attributes on top - those depend on the real recommendations
    // section's own horizontalScroller setting, which
    // sections/recently-viewed-card.liquid has no access to.
    function renderRecentlyViewedPanel(panel, grid, root, iconsTemplate) {
        var scroll = resolveScroll(root.dataset.scroller);

        if (scroll.horizontalScroll) {
            scroll.horizontalScroll.split(' ').forEach(function (cls) {
                if (cls) grid.classList.add(cls);
            });
        }
        if (scroll.hscrollSlide) {
            grid.setAttribute('hscroll-area', '');
            Array.prototype.forEach.call(grid.children, function (card) {
                card.setAttribute('hscroll-slide', '');
            });
        }

        var viewport = document.createElement('div');
        viewport.className = 'product-recommendations__viewport';
        viewport.appendChild(grid);

        if (scroll.hscrollSlide && iconsTemplate) {
            var prevIcon = iconsTemplate.content.querySelector('[data-icon="arrow-left"]');
            var nextIcon = iconsTemplate.content.querySelector('[data-icon="arrow-right"]');

            var prevBtn = document.createElement('button');
            prevBtn.type = 'button';
            prevBtn.className = 'product-recommendations__nav-btn is-prev';
            prevBtn.setAttribute('hscroll-button-prev', '');
            prevBtn.setAttribute('aria-label', root.dataset.labelPrev || 'Previous');
            if (prevIcon) prevBtn.innerHTML = prevIcon.innerHTML;

            var nextBtn = document.createElement('button');
            nextBtn.type = 'button';
            nextBtn.className = 'product-recommendations__nav-btn is-next';
            nextBtn.setAttribute('hscroll-button-next', '');
            nextBtn.setAttribute('aria-label', root.dataset.labelNext || 'Next');
            if (nextIcon) nextBtn.innerHTML = nextIcon.innerHTML;

            viewport.appendChild(prevBtn);
            viewport.appendChild(nextBtn);
        }

        var wrapper = viewport;
        if (scroll.hscrollSlide) {
            var loader = document.createElement('component-loader');
            var slider = document.createElement('hscroll-slider');
            slider.appendChild(viewport);
            loader.appendChild(slider);
            wrapper = loader;
        }

        // One single insertion into the live DOM, with everything already
        // built and populated.
        panel.innerHTML = '';
        panel.appendChild(wrapper);

        // panel may still be `hidden` at this point (it only becomes visible
        // once its tab is selected, or via the no-recommendations fallback
        // below) - either path already calls refreshHscrollSliders() itself
        // once the panel is actually unhidden.
    }

    function wireTabs(section) {
        var tabs = section.querySelectorAll('.product-recommendations__tab');
        var panels = section.querySelectorAll('.product-recommendations__panel');
        if (!tabs.length || !panels.length) return;

        tabs.forEach(function (tab) {
            tab.addEventListener('click', function () {
                if (tab.hidden || tab.getAttribute('aria-selected') === 'true') return;
                tabs.forEach(function (t) {
                    var active = t === tab;
                    t.classList.toggle('is-active', active);
                    t.setAttribute('aria-selected', active ? 'true' : 'false');
                });
                panels.forEach(function (panel) {
                    panel.hidden = panel.dataset.panel !== tab.dataset.tab;
                });
                // The panel we just unhid may contain an hscroll-slider that
                // first measured itself while display:none - force it (and
                // any other slider on the page) to re-measure now that it
                // has real dimensions.
                refreshHscrollSliders();
            });
        });
    }

    function loadRecentlyViewed(root, section, currentHandle) {
        var recentlyViewedTab = section.querySelector('.product-recommendations__tab[data-tab="recently-viewed"]');
        var recentlyViewedPanel = section.querySelector('.product-recommendations__panel[data-panel="recently-viewed"]');
        if (!recentlyViewedTab || !recentlyViewedPanel) return;

        var viewedHandles = getStoredHandles()
            .filter(function (h) {
                return h && h !== currentHandle;
            })
            .slice(0, MAX_SHOWN);

        if (!viewedHandles.length) return;

        var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var iconsTemplate = section.querySelector('template[data-recently-viewed-icons]');

        Promise.all(
            viewedHandles.map(function (handle) {
                return fetchCardMarkup(routesRoot, handle);
            })
        ).then(function (results) {
            var finalGrid = null;
            var invalidHandles = [];

            results.forEach(function (result, i) {
                if (!result) {
                    invalidHandles.push(viewedHandles[i]);
                    return;
                }
                if (!finalGrid) {
                    // Keep the first fetch's grid wrapper as-is - it already
                    // contains its own card, and every fetch's wrapper is
                    // attribute-for-attribute identical since it doesn't
                    // depend on `product`.
                    finalGrid = result.grid;
                } else {
                    finalGrid.appendChild(result.card);
                }
            });

            pruneHandles(invalidHandles);

            if (!finalGrid) return;

            renderRecentlyViewedPanel(recentlyViewedPanel, finalGrid, root, iconsTemplate);
            recentlyViewedTab.hidden = false;

            var recommendedTab = section.querySelector('.product-recommendations__tab[data-tab="recommended"]');
            if (!recommendedTab || recommendedTab.hidden) {
                // No recommendations available - make "Recently seen" the
                // active tab and reveal the section (it starts hidden when
                // there are no recommendations, see the Liquid section).
                if (recommendedTab) recommendedTab.classList.remove('is-active');
                if (recommendedTab) recommendedTab.setAttribute('aria-selected', 'false');
                recentlyViewedTab.classList.add('is-active');
                recentlyViewedTab.setAttribute('aria-selected', 'true');
                recentlyViewedPanel.hidden = false;
                section.removeAttribute('hidden');
                // Same re-measure as the tab-click path - this panel is
                // being unhidden here instead of via wireTabs' click
                // handler, so it needs its own trigger.
                refreshHscrollSliders();
            }
        });
    }

    function setup(root) {
        var section = root.closest('section.related.products');
        if (!section) return;

        var currentHandle = root.getAttribute('data-product-handle');
        wireTabs(section);
        loadRecentlyViewed(root, section, currentHandle);
    }

    function init() {
        var root = document.querySelector('product-recommendations[data-product-id]');
        if (!root) return;

        recordCurrentProduct(root.getAttribute('data-product-handle'));
        setup(root);

        // The section's own recommendations are fetched asynchronously and
        // swap this element's contents once loaded (see data-url on
        // <product-recommendations>) - re-wire everything whenever that
        // happens, not just on first load.
        var observer = new MutationObserver(function () {
            setup(root);
        });
        observer.observe(root, { childList: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
